<template>
  <SoAlert
    v-if="query?.isError"
    type="error"
    title="Failed to load configurations"
    data-testid="service-config-list-error"
  />

  <div v-else>
    <SoTextField
      v-model="search"
      placeholder="Search by name"
      append-icon="search"
      clearable
      hide-details
      class="mb-4"
      :style="{ maxWidth: '320px' }"
      data-testid="service-config-search"
    />

    <SoDataTable
      :headers="headers"
      :items="filteredItems"
      :loading="isLoading"
      :searchable="false"
      :row-clickable="true"
      :default-items-per-page="50"
      data-testid="service-config-list-table"
      @click:row="onRowClick"
    >
      <template #header.totalCount="{ column }">
        <SoTooltip
          text="The total number of historical configurations. This is both active and inactive."
        >
          {{ column.title }}
        </SoTooltip>
      </template>

      <template #item.isActive="{ internalItem }">
        <ServiceConfigStatusCell
          :active="internalItem.raw.isActive"
          :testid="`service-config-status-${internalItem.raw.name}`"
        />
      </template>

      <template #item.start="{ internalItem }">
        <ServiceConfigDateCell
          :display="internalItem.raw.startDateDisplay"
          :tooltip="internalItem.raw.startDisplay"
          :has-value="!!internalItem.raw.start"
        />
      </template>

      <template #item.end="{ internalItem }">
        <ServiceConfigDateCell
          :display="internalItem.raw.endDateDisplay"
          :tooltip="internalItem.raw.endDisplay"
          :has-value="!!internalItem.raw.end"
        />
      </template>

      <template #item.createdAt="{ internalItem }">
        <ServiceConfigDateCell
          :display="internalItem.raw.createdAtDateDisplay"
          :tooltip="internalItem.raw.createdAtDisplay"
        />
      </template>

      <template #item.updatedAt="{ internalItem }">
        <ServiceConfigDateCell
          :display="internalItem.raw.updatedAtDateDisplay"
          :tooltip="internalItem.raw.updatedAtDisplay"
          :has-value="!!internalItem.raw.updatedAt"
        />
      </template>
    </SoDataTable>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import ServiceConfigStatusCell from "@/views/admin/serviceConfig/components/ServiceConfigStatusCell.vue"
  import ServiceConfigDateCell from "@/views/admin/serviceConfig/components/ServiceConfigDateCell.vue"
  import { dateTimeColumn } from "@/views/admin/serviceConfig/components/serviceConfigColumns"
  import {
    SoDataTableHeader,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import {
    ServiceConfigService,
    ConfigListRow,
  } from "@/services/admin/ServiceConfigService"
  import { QueryResult } from "@/api/utils/types"
  import { RouteNames } from "@/types/router"

  type ConfigRow = ConfigListRow & SoDataTableItem

  const headers: SoDataTableHeader[] = [
    { title: "Name", key: "name", sortable: true },
    { title: "Value", key: "value", sortable: true },
    { title: "Total", key: "totalCount", sortable: true },
    { title: "Status", key: "isActive", sortable: true },
    dateTimeColumn("Start", "start"),
    dateTimeColumn("End", "end"),
    dateTimeColumn("Created", "createdAt"),
    dateTimeColumn("Updated", "updatedAt"),
  ]

  export default defineComponent({
    components: {
      SoAlert,
      SoTooltip,
      SoTextField,
      SoDataTable,
      ServiceConfigStatusCell,
      ServiceConfigDateCell,
    },
    data() {
      return {
        headers,
        search: "",
        query: null as QueryResult<ConfigListRow[]> | null,
      }
    },
    computed: {
      isLoading(): boolean {
        return this.query?.isFetching ?? false
      },
      items(): ConfigRow[] {
        return (this.query?.data ?? []) as ConfigRow[]
      },
      filteredItems(): ConfigRow[] {
        const query = this.search?.trim().toLowerCase()
        if (!query) return this.items
        return this.items.filter((item) =>
          item.name.toLowerCase().includes(query),
        )
      },
    },
    mounted() {
      this.query = ServiceConfigService.getConfigs()
    },
    methods: {
      onRowClick(row: ConfigListRow) {
        this.$router.push({
          name: RouteNames.AdminServiceConfigDetail,
          params: {
            serviceName: this.$route.params.serviceName,
            configName: row.name,
          },
        })
      },
    },
  })
</script>
